import { Box, Button, Grid, Skeleton, Typography } from "@mui/material";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import { get, includes, isEmpty, keys } from "lodash";
import Card from "components/card/Card";
import { StyledRooms } from "./styled";
import { useNavigate, useParams } from "react-router-dom";
import { LanguageContext } from "components/language";
import { useDispatch, useSelector } from "react-redux";
import { changeRateField } from "app/reducers/rate";
import { filterSelector, styleSelector } from "app/selectors";
import { PARENT_ORIGIN } from "App";
import { setTargetCategory } from "app/reducers/filter";
import CheckIcon from "@mui/icons-material/Check";
import classNames from "classnames";
import { increase } from "utils/functions";
import Rates from "components/rates/Rates";

const containerSx = {
    marginInline: "auto",
    maxWidth: {
        xxl: "1296px",
        xl: "972px",
        lg: "924px",
        md: "721px",
        xs: "517px",
    },
};

const Rooms = ({
    categories = {},
    rates = [],
    roomLoading = false,
    targetCategoryId,
    howManyDays,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { hotelId, lan } = useParams();
    const usedRoomsCount = useRef({});
    const { t } = useContext(LanguageContext);
    const design = useSelector(styleSelector);
    const filterData = useSelector(filterSelector);
    const [currentRate, setCurrentRate] = useState({ index: 0 });
    const [{ nomer = "ROOM_1", selected = {} }, setState] = useState({});

    const rooms = useMemo(
        () => get(categories, `${nomer}.${currentRate?.rateId}.categories`, []),
        [currentRate, categories, nomer]
    );
    const nomers = useMemo(() => Object.keys(categories), [categories]);

    useEffect(() => {
        if (rates.length > 0) {
            setCurrentRate({ ...rates[0], index: 0 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rates.length]);

    useEffect(() => {
        if (!isEmpty(selected)) {
            usedRoomsCount.current = {};
            setState((s) => ({ ...s, selected: {} }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories]);

    useEffect(() => {
        if (targetCategoryId) dispatch(setTargetCategory(targetCategoryId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isEmpty(rates)) return null;

    const chooseNomer = (i) => {
        const name = "ROOM_" + (i + 1);
        const index = selected[name] ? get(selected[name], "rate.index") : 0;
        setState((s) => ({ ...s, nomer: name }));
        setCurrentRate({ ...rates[index], index });
    };

    const goReservation = (categories = selected) => {
        dispatch(
            changeRateField({
                categories,
                currencyCode: currentRate.currencyCode,
            })
        );
        window.parent.postMessage("scrollToTop", PARENT_ORIGIN);
        navigate(`/${lan}/${hotelId}/reservation`);
    };

    const setRoom = (categoryId, categoryName) => {
        const isSelectedRoom = selected[nomer]?.categoryId === categoryId;

        if (isSelectedRoom) {
            // Deselect the room if already selected
            delete selected[nomer];
            usedRoomsCount.current[categoryId]--;
        } else {
            usedRoomsCount.current[categoryId] = increase(
                usedRoomsCount.current[categoryId]
            );

            if (selected[nomer]) {
                usedRoomsCount.current[selected[nomer].categoryId]--;
            }

            // Update the selected room data
            const data = { rate: currentRate, categoryId, categoryName };
            selected[nomer] = data;
        }

        setState((s) => ({ ...s, selected: { ...selected } }));

        if (nomers.every((n) => !!selected[n])) {
            goReservation(selected);
        } else {
            const nomerName = nomers.find((n) => !selected[n]);
            if (!nomerName) return;
            const [, ind] = nomerName.split("_");
            chooseNomer(ind - 1);
        }
    };

    const hasAvailableRoom = (room) => {
        if (selected[nomer]?.categoryId === room?.id) return true;
        else if (
            room &&
            room?.amountOfAvailableRooms -
                (usedRoomsCount.current[room.id] || 0) <
                1
        )
            return false;
        return true;
    };

    const handleSelectRate = (rate, index) => {
        setCurrentRate({ ...rate, index });
        if (selected[nomer]) {
            selected[nomer].rate = {
                index,
                id: get(rate, "discountType") ? rate.rateId : rate.id,
            };
            setState((s) => ({ ...s, selected: { ...selected } }));
        }
    };

    const messageForCause = get(
        categories,
        `${nomer}.${currentRate?.rateId}.messageForCause`,
        ""
    );
    const minStayDays = get(
        categories,
        `${nomer}.${currentRate?.rateId}.minStayDays`,
        ""
    );
    const minimalStayDays = currentRate?.minimalStayDays;

    return (
        <StyledRooms design={design} sx={containerSx}>
            <Box mb={2}>
                <h1 className="outside">{t("RECOMENDED_ROOMS")}</h1>
            </Box>

            {nomers.length > 1 ? (
                <Box
                    mb={4}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <div className="nomers">
                        {nomers.map((_, i) => (
                            <div
                                key={i}
                                onClick={() => chooseNomer(i)}
                                className={classNames("nomer", {
                                    active: nomer === `ROOM_${i + 1}`,
                                })}
                            >
                                <div className="content">
                                    <span className="name">
                                        {i + 1} {t("ROOM")}
                                    </span>
                                    <div
                                        className={classNames("check", {
                                            active: selected[`ROOM_${i + 1}`],
                                        })}
                                    >
                                        <CheckIcon />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <Button
                        onClick={() => goReservation()}
                        disabled={
                            keys(selected).length !== nomers.length ||
                            !nomers.length
                        }
                        className="bookBtn"
                    >
                        {t("BOOK")}
                    </Button>
                </Box>
            ) : null}

            <Rates
                {...{
                    currentRate,
                    handleSelectRate,
                    rates,
                    t,
                    dateRange: get(filterData, "dateRange", {}),
                }}
            />

            {currentRate?.minimalStayDays <= howManyDays ? (
                <Grid
                    mt={1}
                    container
                    columnSpacing={2}
                    rowSpacing={3}
                    className="rooms"
                >
                    {(roomLoading ? Array.from(new Array(6)) : rooms).map(
                        (room, index) => {
                            if (!hasAvailableRoom(room)) return null;
                            return (
                                <Grid xs={12} lg={6} xxl={4} item key={index}>
                                    {!room ? (
                                        <Box
                                            sx={{
                                                width: "100%",
                                                borderRadius: "10px",
                                                overflow: "hidden",
                                            }}
                                        >
                                            <Skeleton
                                                variant="rectangular"
                                                width={"100%"}
                                                height={200}
                                            />
                                            <Skeleton />
                                            <Skeleton width="60%" />{" "}
                                        </Box>
                                    ) : (
                                        <Card
                                            rate={currentRate}
                                            selected={selected[nomer]}
                                            dateRange={get(
                                                filterData,
                                                "dateRange",
                                                {}
                                            )}
                                            {...{ setRoom, room, design }}
                                        />
                                    )}
                                </Grid>
                            );
                        }
                    )}
                </Grid>
            ) : null}

            {!roomLoading && isEmpty(rooms) ? (
                <Typography mt={4} variant="h5">
                    {includes(messageForCause, "MINIMUM_STAY_DAYS") &&
                    minStayDays > 0
                        ? `${t("MINIMUM_STAY_DAYS")}: ${minStayDays}`
                        : t(messageForCause)}
                </Typography>
            ) : minimalStayDays > howManyDays ? (
                <Typography mt={4} variant="h5">
                    {t("MINIMUM_STAY_DAYS")}: {minimalStayDays}
                </Typography>
            ) : null}
        </StyledRooms>
    );
};

export default Rooms;
